/**
 * Gandiv's band — the CLN higher-timeframe body AS IT WAS KNOWN at each bar.
 *
 * The CLN context band (utils/clnHtfBand.ts) stamps every lower-timeframe bar
 * with the FINAL regression body of the HTF bar covering it. That body is only
 * final when the HTF bar closes, so in history the context band shows values
 * nobody had at the time. Drawn as context that is fine; a rule that decides on
 * it repaints (the Vajra look-ahead: +1.48% of pure hindsight).
 *
 * The obvious fix — the last CLOSED HTF bar (`stampHtfLevels`) — is causal but
 * reacts a whole HTF bar late. The owner chose this instead (2026-09-24):
 *
 *   for LTF bar t inside HTF window w, the body is computed from every HTF bar
 *   that closed before w PLUS window w as it stood when t closed:
 *     open  = the window's open (known the moment the window opens)
 *     close = t's REAL close
 *
 * Only open and close enter the regression (`linreg(open,N)` / `linreg(close,N)`,
 * exactly as computeClnBars), so the window's future high/low/close is never
 * read. Once t has closed its value can never change — and at the LAST bar of a
 * window, whose close IS the window's close, it equals the final body the
 * context band draws. Asserted in backend/src/scripts/verifyGandiv.ts.
 *
 * BAR REPLAY needs no special case. The fetched HTF series is not replay-cut,
 * but a bar only ever reads HTF bars that CLOSED before its own window, and the
 * current window's OPEN. The LTF bars are cut by the replay already.
 *
 * WARM-UP. `linreg` fits over fewer bars while the history is short, so those
 * values depend on where the fetch happened to start — and the fetch window
 * slides. No band is produced until N HTF bars exist, which makes every value
 * independent of the fetch start.
 *
 * JOINED ON BAR TIME, NEVER ON INDEX. Times are the chart's IST-shifted seconds.
 */
import type { CandleRow } from '../components/charts/TradingChart';
import { htfBarCloseTime } from './oscHtf';

export interface GandivBandPoint {
  time: number;
  upper: number;
  lower: number;
  /** The as-known regression direction (lrClose >= lrOpen). */
  up: boolean;
  /** When the covering HTF window opened. */
  windowStart: number;
}

/** Same clamp as computeClnBars, so "length 50" means the same thing on both. */
export function gandivClnLength(length: number): number {
  return Math.max(2, Math.min(200, Math.round(length) || 14));
}

/**
 * The fitted value at the LAST of `ys` over exactly `ys.length` points. The loop
 * is `linreg`'s own (utils/candleTransforms.ts), in the same order, so for a full
 * window the result is bit-identical to `linreg(src, N)[i]`.
 */
function linregLast(ys: number[]): number {
  const n = ys.length;
  if (n === 1) return ys[0];
  const sumX = (n * (n - 1)) / 2;
  const sumXX = (n * (n - 1) * (2 * n - 1)) / 6;
  const denom = n * sumXX - sumX * sumX;
  let sumY = 0, sumXY = 0;
  for (let x = 0; x < n; x++) {
    const y = ys[x];
    sumY += y;
    sumXY += x * y;
  }
  const b = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const a = (sumY - b * sumX) / n;
  return a + b * (n - 1);
}

/**
 * @param ltfBars   the chart's REAL closed bars, ascending (never a display transform)
 * @param htfRaw    the fetched mapped-HTF bars, ascending; the forming one may be
 *                  included. Where the chart has bars for a window, they win;
 *                  fetched values only fill windows older than the LTF history
 * @param htfMin    HTF bar size in minutes
 * @param clnLength regression length (settings.candle.clnLength)
 * @returns one entry per LTF bar, index-aligned to `ltfBars`, null where no band
 */
export function computeGandivBand(
  ltfBars: CandleRow[],
  htfRaw: CandleRow[],
  htfMin: number,
  clnLength: number,
): Array<GandivBandPoint | null> {
  const out: Array<GandivBandPoint | null> = new Array(ltfBars ? ltfBars.length : 0).fill(null);
  if (!ltfBars?.length || !htfRaw?.length || !(htfMin > 0)) return out;
  const N = gandivClnLength(clnLength);

  // One entry per HTF window, oldest first: every fetched window, plus windows
  // the fetch does not cover yet (the HTF refetches every few minutes, the LTF
  // every few seconds). Those continue the HTF GRID from the last window's
  // close, so a refetch later finds them exactly where they were assumed.
  //
  // THE CHART'S OWN BARS WIN wherever they fall in a window: its open is the
  // FIRST LTF bar's open inside it, its close the LAST one's close — the chart's
  // bars grouped onto the HTF grid by their open time. Two reasons, both repaints
  // at the live edge that the verifier caught:
  //   - a fetched bar that was still forming carries a PARTIAL close, which sat
  //     in the history of every later bar until the next refetch replaced it;
  //   - a window the fetch has not reached yet has no fetched open, and the LTF
  //     bars need not open on its boundary (this app's 8H bars open at 17:30 IST,
  //     straddling the 05:30 IST day), so a fetched open that arrives later
  //     would move every bar already stamped.
  // Fetched values fill only windows with no LTF bars — older than the chart's
  // history, where no refetch can change anything.
  interface Win {
    start: number;
    end: number;
    fOpen: number | null;
    fClose: number | null;
    /** Open of the first LTF bar inside the window. */
    ltfOpen: number | null;
    /** Close of the latest LTF bar seen inside the window. */
    ltfClose: number | null;
  }
  const wins: Win[] = [];
  const hOpen = (w: Win): number => (w.ltfOpen ?? w.fOpen ?? (w.ltfClose as number));
  const hClose = (w: Win): number => (w.ltfClose ?? w.fClose ?? hOpen(w));

  let f = 0; // next fetched bar not yet in `wins`
  for (let i = 0; i < ltfBars.length; i++) {
    const bar = ltfBars[i];
    const t = Number(bar.time);
    while (f < htfRaw.length && Number(htfRaw[f].time) <= t) {
      const ft = Number(htfRaw[f].time);
      wins.push({ start: ft, end: htfBarCloseTime(ft, htfMin), fOpen: Number(htfRaw[f].open), fClose: Number(htfRaw[f].close), ltfOpen: null, ltfClose: null });
      f++;
    }
    if (!wins.length) continue; // before the first HTF bar opened
    let w = wins[wins.length - 1];
    if (t >= w.end) {
      // Past every fetched window: step along the grid to the one containing t.
      let st = w.end;
      let en = htfBarCloseTime(st, htfMin);
      while (t >= en) { st = en; en = htfBarCloseTime(st, htfMin); }
      w = { start: st, end: en, fOpen: null, fClose: null, ltfOpen: null, ltfClose: null };
      wins.push(w);
    }
    if (w.ltfOpen == null) w.ltfOpen = Number(bar.open);
    w.ltfClose = Number(bar.close);

    const j = wins.length - 1;
    if (j < N - 1) continue;
    const os: number[] = [];
    const cs: number[] = [];
    for (let q = j - (N - 1); q < j; q++) { os.push(hOpen(wins[q])); cs.push(hClose(wins[q])); }
    // The covering window as it stands at this bar's close.
    os.push(hOpen(w));
    cs.push(Number(bar.close));
    const lo = linregLast(os), lc = linregLast(cs);
    out[i] = { time: t, upper: Math.max(lo, lc), lower: Math.min(lo, lc), up: lc >= lo, windowStart: w.start };
  }
  return out;
}
